import React, { useState, useEffect } from "react";
import "./index.css";
import { HeaderProps } from "../../service/interfaces/Components";
import SecureImage from "../BucketImage/Image";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { toggleSidebar } from "../../redux/container/sidebarSlice";

const Header: React.FC<HeaderProps> = ({
  icon,
  profile,
  contact,
  status,
  subtitleId,
  toggleComponent,
  title,
  children,
  subtitle,
  showSearch = true,
  setSearch,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSearchExpanded(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSearch = () => {
    if (isMobile) {
      setIsSearchExpanded((prev) => !prev);
    }
  };

  const handleSidebarToggle = () => {
    dispatch(toggleSidebar());
  };

  return (
    <div
      className={`bg-white font-poppins z-50 flex flex-wrap items-center justify-between shadow-sm fixed px-2 sm:px-4 md:px-6 right-0 transition-all duration-300 ${
        profile ? "py-2" : "py-4"
      }`}
      style={{
        width: isMobile ? "100%" : "calc(100% - 208px)",
        top: 0,
        flexDirection: isSearchExpanded ? "column" : "row",
      }}
    >
      {/* Mobile Sidebar Button */}
      {isMobile && (
        <button
          className="fixed top-4 left-4 z-50 p-2 rounded bg-white  md:hidden"
          onClick={handleSidebarToggle}
          aria-label="Toggle menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      )}

      {/* Left Section: Icon, Title, Info */}
      <div
        className={`flex items-center gap-2 ${isMobile? "ml-[60px]":""} ${
          isSearchExpanded ? "w-full mb-3" : "flex-shrink min-w-0"
        }`}
      >
        {profile && (
          <img
            src={profile}
            className="rounded-full h-10 w-10 md:h-[62px] md:w-[62px] object-cover"
            alt="Profile"
          />
        )}
        {icon && (
          <SecureImage
            fileKey={icon}
            alt="Header Icon"
            width="32px"
            className="h-6 w-6 md:h-8 md:w-8 object-contain flex-shrink-0"
            style={{
              filter:
                "invert(27%) sepia(51%) saturate(1753%) hue-rotate(218deg) brightness(97%) contrast(94%)",
            }}
          />
        )}

        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-2xl font-semibold text-gray-800 truncate">
              {title}
            </h1>
            {status && <div className="ml-1">{status}</div>}
          </div>
          {subtitle && (
            <p className="text-sm md:text-base font-semibold text-blue-800 truncate">
              {subtitle}
            </p>
          )}
          {subtitleId && (
            <p className="text-sm md:text-base font-normal text-gray-600 truncate">
              {subtitleId}
            </p>
          )}
          {contact && (
            <p className="text-xs md:text-sm text-gray-700 truncate">
              {contact}
            </p>
          )}
        </div>
      </div>
    
      {/* Right Section: Search + Children */}
      <div className="relative w-full md:w-auto ">
  {toggleComponent && toggleComponent}
</div>

      <div
        className={`flex items-center gap-2 md:gap-4 ${
          isSearchExpanded ? "w-full" : "flex-shrink-0"
        }`}
      >
        {/* Search Toggle Button (Mobile Only) */}
        {showSearch && isMobile && !isSearchExpanded && (
          <button onClick={toggleSearch} className="p-2" aria-label="Open search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        )}
        {/* Search Input */}
        {showSearch && (!isMobile || isSearchExpanded) && (
          <div className="relative w-full md:w-auto">
            <input
              type="text"
              placeholder="Search"
              className="rounded-full border border-gray-300 px-4 py-1 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              onChange={(e) => setSearch?.(e.target.value)}
              autoFocus={isSearchExpanded}
            />
            {isSearchExpanded && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                onClick={toggleSearch}
                aria-label="Close search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Extra Right Side Content */}
        {(!isSearchExpanded || !isMobile) && children}
      </div>
    </div>
  );
};

export default Header;
